import {
  Window,
  WindowActionsBar,
  WindowProps
} from '@progress/kendo-react-dialogs';
import cloneDeep from 'lodash.clonedeep';
import { Button } from '@progress/kendo-react-buttons';
import { ComponentType, useContext, useMemo, useState } from 'react';
import { InputChangeEvent } from '@progress/kendo-react-inputs';
import { ComboBoxChangeEvent } from '@progress/kendo-react-dropdowns';

import { GetResourceValue } from '../../../utils/game-document';
import { EditorMode } from '../../../types/editor-mode';
import RequiredFields from '../../../types/required-fields';
import { ComboboxInput } from '../../../components/form-input';
import { TaskEntity } from '../../../types/game-document/entities';
import { FluidForm, StandardInput } from '../../../components/forms';
import { GameDocumentContext } from '../../../contexts/game-document';
import { TaskRoute } from '../../../types/game-document/entities/routes';
import { EntityEditor } from '../../../types/game-document/entity-editor';

interface RouteEditorWindowProps extends WindowProps {
  editorEntity: EntityEditor<TaskRoute>;
  toggleDialog: Function;
  onSubmit: (editorEntity: EntityEditor<TaskRoute>, titleValue?: string) => void;
  editorMode: EditorMode;
  tasks?: TaskEntity[];
  routes?: TaskRoute[];
}

export const RouteEditorWindow: ComponentType<RouteEditorWindowProps> = ({
  editorEntity,
  toggleDialog,
  onSubmit,
  editorMode,
  ...props
}: RouteEditorWindowProps) => {
  const [state] = useContext(GameDocumentContext);
  const [entity, setEntity] = useState<TaskRoute>(
    cloneDeep(editorEntity.entity)
  );

  const initialTitle = useMemo(() => {
    if (editorEntity.isNew || !editorEntity.entity.titleResId || !state.gameDocument) {
      return '';
    }
    return GetResourceValue(state.gameDocument, editorEntity.entity.titleResId) || '';
  }, [editorEntity, state.gameDocument]);

  const [titleValue, setTitleValue] = useState<string>(initialTitle);

  const [requiredFields, setRequiredFields] = useState<
    RequiredFields<TaskRoute>[]
  >([
    { name: 'titleResId', errorMessage: '' },
    { name: 'toTaskId', errorMessage: '' }
  ]);

  const task = useMemo(() => {
    if (!props.tasks || !state.gameDocument) return [];
    return props.tasks?.map((t) => ({
      ...t,
      title: GetResourceValue(state.gameDocument!, t.titleResId ?? '')
    }));
  }, [state.gameDocument, props.tasks]);

  const handleInputChange = (event: InputChangeEvent) => {
    const { name, value } = event.target;

    if (name === 'name') {
      setEntity({ ...entity, name: value as string });
    } else if (name === 'titleResId') {
      setTitleValue(value as string);
    }
  };

  const handleComboboxChange = (event: ComboBoxChangeEvent) => {
    const { name, value } = event.target;

    setEntity({ ...entity, [name!]: (value! as unknown as TaskEntity).id });
  };

  const isInputValid = (): boolean => {
    const inputValidation = requiredFields.map(
      (input: RequiredFields<TaskRoute>) => {
        input.errorMessage = '';
        if (input.name === 'titleResId') {
          if (!titleValue || titleValue.trim() === '') {
            input.errorMessage = 'This field is required';
          }
        }

        if (input.name === 'toTaskId') {
          if (!entity.toTaskId) {
            input.errorMessage = 'This field is required';
          } else if (entity.fromTaskId === entity.toTaskId) {
            input.errorMessage = 'Task start and Task end cannot be the same';
          }
        }

        return input;
      }
    );
    setRequiredFields(inputValidation);
    return (
      inputValidation.findIndex((input) => input.errorMessage !== '') === -1
    );
  };

  return (
    <Window
      modal={true}
      minimizeButton={undefined}
      className={'shadow'}
      initialWidth={editorEntity.isNew ? 500 : 700}
      initialHeight={editorEntity.isNew ? 450 : 650}
      title={
        editorEntity.isNew
          ? 'New annotation'
          : titleValue || GetResourceValue(state.gameDocument!, entity.titleResId)
      }
      {...props}>
      <FluidForm>
        <div className={'d-flex flex-row'}>
          <div className={'flex-column flex-fill'}>
            <StandardInput
              name={'titleResId'}
              className={'mb-2'}
              label={'Title'}
              validationMessage={
                requiredFields?.find((task) => task.name === 'titleResId')
                  ?.errorMessage ?? ''
              }
              value={titleValue}
              onChange={(e) => handleInputChange(e)}
            />
            <ComboboxInput
              label={'Task start'}
              placeholder={'Select a task'}
              data={task.filter((x) => x.id !== entity.toTaskId) ?? []}
              textField={'title'}
              value={task.find((x) => x.id === entity.fromTaskId)}
              name={'fromTaskId'}
              onChange={handleComboboxChange}
              clearButton={false}
            />
            <ComboboxInput
              label={'Task end'}
              placeholder={'Select a task'}
              data={task.filter((x) => x.id !== entity.fromTaskId) ?? []}
              textField={'title'}
              value={task.find((x) => x.id === entity.toTaskId)}
              name={'toTaskId'}
              clearButton={false}
              onChange={handleComboboxChange}
              validationMessage={
                requiredFields?.find((task) => task.name === 'toTaskId')
                  ?.errorMessage ?? ''
              }
            />
          </div>
        </div>
      </FluidForm>
      <WindowActionsBar>
        <Button themeColor={'secondary'} onClick={() => toggleDialog()}>
          Cancel
        </Button>
        <Button
          themeColor={'primary'}
          onClick={() => {
            if (isInputValid()) {
              const updatedEntity = { ...entity };
              if (updatedEntity.automaticallyInfo) {
                if (titleValue.trim() !== initialTitle.trim()) {
                  updatedEntity.isTitleCustomized = true;
                }
              }
              onSubmit({ isNew: editorEntity.isNew, entity: updatedEntity }, titleValue);
            }
          }}>
          Save
        </Button>
      </WindowActionsBar>
    </Window>
  );
};
